import { Box, FormLabel, useToast } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { PhysicalQuantity } from "@nens/rana/api-v1";
import { RanaSelect } from "@nens/ranalib";
import { capitalizeFirst, objectValues } from "@nens/shared-utils";

import { usePublicationStore } from "@/features/publications/_internal";
import { RasterLayer } from "@/features/publications/types/publication";
import { useStylingOptionsQueryDirectly } from "@/queries/query-styling-options";

export const RasterPhysicalQuantityDropdown = ({ layer }: { layer: RasterLayer }) => {
  const toast = useToast();
  const intl = useIntl();
  const fetchStylingOptions = useStylingOptionsQueryDirectly();
  const replaceLayer = usePublicationStore((state) => state.replaceLayer);

  const onChange = async (physicalQuantity: PhysicalQuantity) => {
    const { isSuccess, data } = await fetchStylingOptions(physicalQuantity);
    if (!isSuccess) {
      toast({
        title: intl.formatMessage({
          id: "error",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "publication.raster_physical_quantity.fetch_styling_options_error_description",
          defaultMessage: "An error occurred while fetching styling options. Please try again.",
        }),
        status: "error",
      });
      return;
    }

    const styleId = data[physicalQuantity].style_id;
    const minMax = data[physicalQuantity].min_max ?? layer.properties.minMax;

    if (layer.type === "layer")
      replaceLayer({
        ...layer,
        properties: { type: "raster", minMax, initialMinMax: minMax, physicalQuantity, styleId },
      });
  };

  return (
    <Box>
      <FormLabel>
        <FormattedMessage id="raster_physical_quantity" defaultMessage="Raster Physical Quantity" />
      </FormLabel>
      <RanaSelect
        w="full"
        value={layer.properties.physicalQuantity}
        onChange={onChange}
        options={objectValues(PhysicalQuantity).map((style) => ({
          label: capitalizeFirst(style.replace(/_/g, " ")),
          value: style,
        }))}
      />
    </Box>
  );
};
